import cn from 'classnames';

import { useCheckoutContext } from '@zooplus/checkout-state';
import { isBitiba } from '@zooplus/commons-lang';
import { H1, P1, PrimaryButton } from '@zooplus/ui-components';

import AlertComponent from '../../../common/notifications/alert-component';
import FeedbackMessage from '../../../common/notifications/feedback-message';

import { getAppConfig } from '@config/get-app-config';
import useCartTracking from '@hooks/use-adobe-tracking/use-cart-tracking';
import { useTranslation } from '@hooks/use-translations';
import emptyBoxBitiba from '@public/images/empty-box-bitiba.png';
import rebrandedEmptyBoxZooplusJpg from '@public/images/rebranded-empty-box-zooplus.jpg';
import rebrandedEmptyBoxZooplusWebp from '@public/images/rebranded-empty-box-zooplus.webp';
import { MESSAGE_PLACEMENT, CHECKOUT_STATE_PROJECT_ID } from '@utilities/constants';
import { isObjectNotNullOrEmpty, getSearchParams } from '@utilities/utils';

import styles from './cart-empty-section.module.scss';

const CartEmptySection = () => {
  const { t } = useTranslation();
  const {
    isFetching,
    prevArticle,
    mainTogglesMap = {},
  } = useCheckoutContext(CHECKOUT_STATE_PROJECT_ID);
  const { trackContinueShoppingEvent } = useCartTracking();
  const { site } = getAppConfig();

  const isNewOverviewPage = mainTogglesMap.CHECKOUT_FRONTEND_NEW_OVERVIEW_PAGE;

  const isBitibaShop = isBitiba(site.domain);

  const articleId =
    getSearchParams(window.location.href).get('articleId') ||
    (isNewOverviewPage && isObjectNotNullOrEmpty(prevArticle));

  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search);
  const displayPaymentRouterMessage = ['conflict', 'resub'].some(
    (param) => params.get(param) === 'true',
  );

  const continueShopping = () => {
    trackContinueShoppingEvent();
    window.location.href = '/';
  };

  return (
    <section className={styles.container} data-testid="checkout-empty-cart">
      {articleId && (
        <FeedbackMessage
          customStyle={styles.alert}
          placement={MESSAGE_PLACEMENT.CART_EMPTY_PAGE_HEADER}
        />
      )}
      {displayPaymentRouterMessage && (
        <AlertComponent
          dataTestId="payment-router-message"
          customStyle={cn(styles.alert, styles.commonStyle)}
          message={t('checkout.cart.payment.router.message')}
        />
      )}

      <article className={styles.content}>
        <H1>{t('cart.empty.heading')}</H1>
        <P1>{t('cart.empty.message')}</P1>
        <div className={styles.buttonContainer}>
          {!isFetching && (
            <PrimaryButton onClick={continueShopping} data-testid="continue-shopping-btn">
              {t('cart.empty.homepage.button')}
            </PrimaryButton>
          )}
        </div>
        {isBitibaShop ? (
          <img
            loading="lazy"
            className={styles.box}
            src={emptyBoxBitiba}
            alt={t('cart.empty.box.altText')}
            data-testid="empty-box"
          />
        ) : (
          <picture>
            <source type="image/webp" srcSet={rebrandedEmptyBoxZooplusWebp} />
            <img
              src={rebrandedEmptyBoxZooplusJpg}
              alt={t('cart.empty.box.altText')}
              className={styles.box}
              data-testid="empty-box"
              height="228"
              width="320"
            />
          </picture>
        )}
      </article>
    </section>
  );
};

export default CartEmptySection;
